/**
 * The only place in this plane that renders a credit (platform design §18.1).
 *
 * Two units share this screen and must never be told apart by hue alone: a
 * tenant is charged in tenant credits, a provider call costs provider
 * credits, and a screen that renders either from its own local formatting
 * is a screen that can render one in the other's place with nothing to catch
 * it. Every figure here carries `data-unit` (or is composed from one that
 * does) so the distinction survives a screenshot, a colour-blind reader and
 * greyscale printing alike.
 *
 * `Margin` and `reachOf` are the only two arithmetic operations this plane
 * performs on a credit figure -- a subtraction and a division, each written
 * once so it cannot disagree with itself. Every other function here is
 * formatting, not arithmetic.
 */

import type { ReactNode } from 'react';

import { Absent } from '../../chrome/measure';
import { defineCatalogue, getLocale, type Locale } from '../../i18n';
import { credits as formatCredits, days as formatDays } from '../../i18n/format';
import { Figure } from './Figure';

export type CreditUnit = 'tenant' | 'provider';

/**
 * This file's own small catalogue -- the words `reachOf`'s two non-numeric
 * outcomes need, and the group label `CostGroup` renders. Not the global
 * catalogue (`src/i18n/pt-BR.ts`): that one is a shared append target across
 * every feature area, and nothing here needs a key any other file would
 * also want to add to.
 */
const unitsCatalogue = defineCatalogue(
  {
    unpriced: 'sem preço',
    noBurn: 'sem consumo',
    nothingBilled: 'Nada foi cobrado no período',
    upTo: 'até {days}',
    'unit.tenant': 'Crédito da organização',
    'unit.provider': 'Crédito SocialCrawl',
    // Short forms printed beside a figure, not in a tooltip: the second word
    // is literally `unit.provider`'s own "SocialCrawl", so the two still read
    // apart with no colour behind either of them.
    'unit.tenant.short': 'cr',
    'unit.provider.short': 'cr SC',
    'costGroup.label': 'custo do provedor',
  },
  {
    unpriced: 'unpriced',
    noBurn: 'no burn',
    nothingBilled: 'Nothing was charged in this period',
    upTo: 'up to {days}',
    'unit.tenant': 'Organisation credit',
    'unit.provider': 'SocialCrawl credit',
    'unit.tenant.short': 'cr',
    'unit.provider.short': 'cr SC',
    'costGroup.label': 'provider cost',
  },
);
const unitsT = unitsCatalogue.t;

/** "+1.000" / "-1.000": the sign is always printed, the magnitude always
 *  grouped through `Intl` in the active locale. Shared by `Credits`'
 *  `signed` branch and by `Margin`, which is signed unconditionally. */
function formatSigned(value: number, locale: Locale): string {
  const sign = value < 0 ? '-' : '+';
  return `${sign}${formatCredits(Math.abs(value), locale)}`;
}

export function Credits(props: {
  value: number | null;
  unit: CreditUnit;
  signed?: boolean;
  /** Distinguishes several credit figures in one scope (e.g. a per-node
   *  provider figure beside a headline total). Defaults to the generic
   *  `"credits"` id every other caller uses. */
  testId?: string;
  /** Prints the unit word beside the figure ("1.000 cr", "1.000 cr SC") so
   *  the tenant/provider identity this file's own doc warns about surviving
   *  "hue alone" also survives in plain text, not only in `data-unit`. Off
   *  by default -- every caller that does not pass it keeps the bare figure
   *  it has always rendered. */
  showUnit?: boolean;
}): ReactNode {
  // Read on every render rather than defaulted: this component is consumed
  // by every screen here, and a locale baked in here would be a second,
  // silently stale answer to "what locale is active" the moment any of them
  // called `setLocale`.
  const locale = getLocale();
  const text =
    props.value === null
      ? unitsT('unpriced') // never 0 -- an unpriced operation is not a free one
      : props.signed
        ? formatSigned(props.value, locale)
        : formatCredits(props.value, locale);
  const unitWord =
    props.showUnit && props.value !== null ? ` ${unitsT(`unit.${props.unit}.short`)}` : '';
  return (
    <Figure>
      <span data-testid={props.testId ?? 'credits'} data-unit={props.unit}>
        {text}
        {unitWord}
      </span>
    </Figure>
  );
}

/**
 * Wraps the provider-credit columns of a table in one bordered, labelled,
 * dimmer group. The border is the primary cue, the reduced weight the
 * secondary one, so the group survives greyscale and a colour-blind reader
 * -- colour encodes meaning only, and "which unit" is an identity, not a
 * meaning.
 */
export function CostGroup(props: { children: ReactNode }): ReactNode {
  return (
    <div
      role="group"
      aria-label={unitsT('costGroup.label')}
      className="border-border/70 rounded border border-dashed font-normal opacity-90"
    >
      {props.children}
    </div>
  );
}

/** Signed, always printed, never clamped: a negative margin is the reason
 *  the price editor exists, and clamping it at zero would hide exactly the
 *  case worth seeing. */
export function Margin(props: {
  tenantCredits: number;
  providerCredits: number;
  /** Distinguishes several margin figures in one scope (e.g. a platform
   *  total beside a per-row margin column). Defaults to the generic
   *  `"margin"` id every other caller uses. */
  testId?: string;
}): ReactNode {
  const diff = props.tenantCredits - props.providerCredits;
  const dataSign = diff < 0 ? 'negative' : diff > 0 ? 'positive' : 'zero';
  return (
    <Figure>
      <span data-testid={props.testId ?? 'margin'} data-sign={dataSign}>
        {formatSigned(diff, getLocale())}
      </span>
    </Figure>
  );
}

/** Margin as a share of what was billed -- still a ratio between two credit
 *  totals (platform design §18.7), never a currency figure. `null` when `tenantCredits` is 0:
 *  a ratio with no denominator is absent, not a manufactured 0% or 100%. */
export function marginPercent(tenantCredits: number, providerCredits: number): number | null {
  if (tenantCredits === 0) return null;
  return ((tenantCredits - providerCredits) / tenantCredits) * 100;
}

export function MarginPercent(props: {
  tenantCredits: number;
  providerCredits: number;
}): ReactNode {
  const pct = marginPercent(props.tenantCredits, props.providerCredits);
  return (
    <Figure>
      <span data-testid="margin-percent">
        {/* Through `Absent`, not a bare glyph: it hides the dash from the
            accessibility tree and says why instead, which is what every other
            absent figure in this plane does. */}
        {pct === null ? (
          <Absent reason={{ text: unitsT('nothingBilled') }} />
        ) : (
          `${Math.round(pct)}%`
        )}
      </span>
    </Figure>
  );
}

/** platform design §18.5's funded window, in the form an operator decides a grant on. `null`
 *  (unpriced) must not read as a shorter, safer reach than the truth, `<= 0`
 *  (no-burn) must not read as infinite, and a balance at or below zero
 *  reaches no further without going negative itself. */
export type Reach = { kind: 'days'; days: number } | { kind: 'unpriced' } | { kind: 'no-burn' };

export function reachOf(availableCredits: number, tenantCreditsPerDay: number | null): Reach {
  if (tenantCreditsPerDay === null) return { kind: 'unpriced' };
  if (tenantCreditsPerDay <= 0) return { kind: 'no-burn' };
  if (availableCredits <= 0) return { kind: 'days', days: 0 };
  // Floored, never rounded: this is the figure a grant gets decided on, and
  // rounding up over-promises the one number that matters.
  return { kind: 'days', days: Math.floor(availableCredits / tenantCreditsPerDay) };
}

/** Whole days, floored, with the word beside the figure -- never a clock,
 *  never a fraction, and marked as an upper bound (`data-bound="upper"`):
 *  platform design §18.5's real window is computed after the planner has shed accounts, so
 *  this figure is the longest the window can be, never the shortest. */
export function ReachFigure(props: { reach: Reach }): ReactNode {
  const locale = getLocale();
  const text =
    props.reach.kind === 'unpriced'
      ? unitsT('unpriced')
      : props.reach.kind === 'no-burn'
        ? unitsT('noBurn')
        : unitsT('upTo', { days: formatDays(props.reach.days, locale) });

  return (
    <Figure>
      <span data-testid="reach" data-bound={props.reach.kind === 'days' ? 'upper' : undefined}>
        {text}
      </span>
    </Figure>
  );
}
